/**
 * Validates that all Questionnaire items marked `required` have answers in the response.
 * Respects `enableWhen` via Medplum's `isQuestionEnabled` (hidden items are skipped).
 *
 * `itemPath` values match TemplatePreviewDrawer `buildItemPath` / `data-item-path` for scroll-to.
 */

import type {
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';
import { isQuestionEnabled, QuestionnaireItemType } from '@medplum/react-hooks';
import { isSignatureDateQuestion } from '@unify/fhir-model/signature-date';

export interface MissingRequiredField {
  /** Slash-separated path (same as `data-item-path` on preview anchors). */
  itemPath: string;
  label: string;
}

/** Same rule as TemplatePreviewDrawer `buildItemPath`. */
export const buildQuestionnaireItemPath = (
  linkId: string | undefined,
  parentPath: string,
): string => {
  const segment = linkId ?? '';
  return parentPath ? `${parentPath}/${segment}` : segment;
};

function stripHtml(s: string): string {
  return s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function isNonEmptyRichOrPlain(s: string | undefined): boolean {
  if (s === undefined || s === null) return false;
  return stripHtml(s).length > 0;
}

function answerValuePresent(
  a: NonNullable<QuestionnaireResponseItem['answer']>[number],
): boolean {
  if (a.valueBoolean !== undefined) return true;
  if (a.valueInteger !== undefined && !Number.isNaN(a.valueInteger)) return true;
  if (a.valueDecimal !== undefined && !Number.isNaN(a.valueDecimal)) return true;
  if (a.valueDate) return true;
  if (a.valueDateTime) return true;
  if (a.valueTime) return true;
  if (a.valueUri && a.valueUri.length > 0) return true;
  if (a.valueString !== undefined) return isNonEmptyRichOrPlain(a.valueString);
  if (a.valueCoding) return true;
  if (a.valueAttachment?.url || a.valueAttachment?.data) return true;
  if (a.valueReference?.reference) return true;
  if (a.valueQuantity !== undefined) return true;
  return false;
}

function responseItemHasAnswer(ri: QuestionnaireResponseItem | undefined): boolean {
  if (!ri?.answer || ri.answer.length === 0) return false;
  return ri.answer.some(answerValuePresent);
}

function collectMissingRequired(
  qItems: QuestionnaireItem[],
  responseItems: QuestionnaireResponseItem[] | undefined,
  qr: QuestionnaireResponse,
  parentPath: string,
): MissingRequiredField[] {
  const missing: MissingRequiredField[] = [];
  const rItems = responseItems ?? [];

  for (const item of qItems) {
    if (!isQuestionEnabled(item, qr)) continue;

    const label = (item.text ?? item.linkId ?? 'Question').trim() || item.linkId || 'Question';
    const itemPath = buildQuestionnaireItemPath(item.linkId, parentPath);

    if (item.type === QuestionnaireItemType.display) continue;
    // Server stamps Date Signed on submit; do not block fill for an empty value.
    if (item.type === QuestionnaireItemType.date && isSignatureDateQuestion(item)) continue;

    if (item.type === QuestionnaireItemType.group) {
      const filtered = rItems.filter((ri) => ri.linkId === item.linkId);

      if (item.repeats) {
        if (item.required && filtered.length === 0) {
          missing.push({ itemPath, label });
        }
        const childParent = buildQuestionnaireItemPath(item.linkId, parentPath);
        for (const ri of filtered) {
          missing.push(...collectMissingRequired(item.item ?? [], ri.item ?? [], qr, childParent));
        }
      } else {
        const ri = filtered[0];
        if (item.required && !ri) {
          missing.push({ itemPath, label });
        }
        const childParent = buildQuestionnaireItemPath(item.linkId, parentPath);
        missing.push(...collectMissingRequired(item.item ?? [], ri?.item ?? [], qr, childParent));
      }
      continue;
    }

    if (!item.required) continue;

    const filtered = rItems.filter((ri) => ri.linkId === item.linkId);
    const ri = filtered[0];
    if (!responseItemHasAnswer(ri)) {
      missing.push({ itemPath, label });
    }
  }

  return missing;
}

export type MissingRequiredFieldsOptions = {
  /** Provider-locked / read-only linkIds to exclude from validation. */
  skipLinkIds?: ReadonlySet<string>;
};

function filterSkippedLinkIds(
  missing: MissingRequiredField[],
  skipLinkIds: ReadonlySet<string> | undefined,
): MissingRequiredField[] {
  if (!skipLinkIds?.size) return missing;
  return missing.filter((field) => {
    const linkId = field.itemPath.split('/').pop();
    return !linkId || !skipLinkIds.has(linkId);
  });
}

export const getMissingRequiredFields = (
  questionnaire: { item?: QuestionnaireItem[] } | null | undefined,
  response: QuestionnaireResponse | null | undefined,
  options?: MissingRequiredFieldsOptions,
): MissingRequiredField[] => {
  if (!questionnaire?.item?.length || !response) return [];
  return filterSkippedLinkIds(
    collectMissingRequired(questionnaire.item, response.item, response, ''),
    options?.skipLinkIds,
  );
};

export const getMissingRequiredQuestionLabels = (
  questionnaire: { item?: QuestionnaireItem[] } | null | undefined,
  response: QuestionnaireResponse | null | undefined,
): string[] => {
  return getMissingRequiredFields(questionnaire, response).map((f) => f.label);
};
